import React, { memo, useCallback } from 'react';
import { InternalError } from '../../../../../../Components/InternalError/InternalError';
import EditableAttribute from '../../../../../../Components/Attributes/EditableAttribute/EditableAttribute';
import { getAttributeValueAutoSize } from '../../../../../../Components/Attributes/Utils/AttributeValueAutoSizeUtils';
import { getAssociationUpdateValueObject } from '../../../../../../Model/avenyModelUtils';
import { AttributeValueSizeType } from '../../../../../../Components/Attributes/AttributeValue/AttributeValue';
import { isHeader } from '../../../Utils/defaultColumns';
import { IFieldValidations } from '../BusinessDataForm/Utils/BusinessDataFormUtils';
import {
  IUseAttributeValuesResult,
  useAttributeValues,
} from '../../../Hooks/useAttributeValues';
import { IEditComponentProps } from '../../../../../../Components/Attributes/EditableAttribute/EditComponentTypes';
import { SpacingProps } from '@mui/system';
import { ISuggestion } from '../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';

// Det här är ett litet hack för att skapa något som fungerar som "nominal typing" i Typescript
// Det går bara att få något av typen AttributePath med funktionen checkValidAttributePath
enum AttributePathBrand {}
export type AttributePath = string & AttributePathBrand;

// Än så länge ingen faktisk kontroll av strängen
function checkValidAttributePath(str: string): str is AttributePath {
  return true;
}

export function toPath(path: string): AttributePath {
  if (checkValidAttributePath(path)) {
    return path;
  } else {
    throw new Error(`Invalid attributepath: ${path}`);
  }
}
interface IAttributProps<T>
  extends Pick<
      React.ComponentProps<typeof EditableAttribute>,
      'debounceDelayTime' | 'hideClearIcon'
    >,
    SpacingProps {
  name: keyof T & string;
  customDisplayLabel?: string;
  size?: AttributeValueSizeType;
  fullWidth?: boolean;
  mode?: 'edit' | 'read';
  autoFocus?: boolean;
  isRequired?: boolean;
  onlyValue?: boolean;
  hideOutline?: boolean;
  disableEditMode?: boolean;
  rightAlign?: boolean;
  disableInputPaddingRight?: boolean;
  rightPadding?: number;
  disableChangeMarker?: boolean;
  lineMarker?: boolean;
  inputRef?: React.MutableRefObject<HTMLInputElement | undefined>;
  disabled?: boolean;
  disableDisabledBackgroundColor?: boolean;
  rowID?: string;
  multiline?: boolean;
  maxRows?: number;
  minRows?: number;
  maxInputLength?: number;
  grayTextColor?: boolean;
  tooltipRensa?: string;
  defaultClearValue?: string;
  onFocus?: (event: React.FocusEvent<HTMLElement, Element>) => void;
  disableTabbing?: boolean;
  disableFutureDates?: boolean;
  /** Margin för formControl inne i editcomponenten om den används (bör ses över) */
  formControlMargin?: 'dense' | 'none' | 'normal';
  /** Vilken komponent som ska användas för att visa Attributet */
  editComponent?: React.ComponentType<
    React.PropsWithChildren<IEditComponentProps<T, undefined, true>>
  >;
  /** Värden som går att välja, hämtas default från EnumVvalues i modellen */
  options?: ISuggestion[];
  onBeforeClear?: () => void;
}

interface IBaseAttributProps<T> extends IAttributProps<T> {
  attributeValues: IUseAttributeValuesResult;
}

function BaseAttribut<T>(
  props: IBaseAttributProps<T>
): React.ReactElement<any> | null {
  const {
    value,
    valueEdited,
    isValid,
    attribute,
    attributeResultError,
    validations,
    editMode,
    model,
    formStateDispatch,
    data,
    editCancelled,
    touched,
    dataLoading,
    savedValue,
    masked,
  } = props.attributeValues;

  //Om det finns en hint om att fältet är required
  const validationRequiredValueHint = validations.find(
    val => val.type === 'RequiredValueHint'
  );
  const validationIsRequired = Boolean(validationRequiredValueHint);
  const validationIsRequiredMessage = validationRequiredValueHint?.message;

  const required = attribute?.Required || validationIsRequired;

  const errorMessage = validations
    .filter(
      val =>
        ((val.type === 'Error' && !val.showAsHint) ||
          (val.type === 'RequiredValueHint' && !val.showAsHint && !value)) &&
        val.message != undefined
    )
    .map(val => val.message)
    .concat([
      ...(required && !value && touched
        ? [validationIsRequiredMessage ?? 'Värde krävs']
        : []),
    ])
    .join(', ');
  const warningMessage = validations
    .filter(val => val.type === 'Warning' && !val.showAsHint)
    .map(val => val.message)
    .join(', ');
  const hintMessage = validations
    .filter(val => val.type === 'Hint' || val.showAsHint)
    .map(val => val.message)
    .join(', ');

  const onChange = useCallback(
    (value: string, validationErrorMessage?: IFieldValidations) => {
      if (formStateDispatch) {
        formStateDispatch({
          type: 'UPDATE',
          value: getAssociationUpdateValueObject(
            props.name as string,
            value,
            data ?? ({} as any)
          ),
          validation: validationErrorMessage,
        });
      }
    },
    [formStateDispatch, props.name, data]
  );

  const onBlur = useCallback(() => {
    if (formStateDispatch) {
      formStateDispatch({
        type: 'TOUCH',
        path: [props.name],
      });
    }
  }, [formStateDispatch, props.name]);
  if (model) {
    if (attribute) {
      const validMode =
        props.mode === undefined ||
        (props.mode === 'edit' && editMode) ||
        (props.mode === 'read' && !editMode);
      if (isValid && validMode) {
        return (
          <EditableAttribute
            collection={model}
            attribute={attribute}
            value={value}
            label={props.customDisplayLabel}
            editMode={!props.disableEditMode && editMode}
            disabled={!attribute.GUIEditable || props.disabled}
            masked={masked}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={props.onFocus}
            size={props.size ?? getAttributeValueAutoSize(attribute)}
            header={isHeader(model.CollectionName, attribute.AttributeName)}
            autoFocus={props.autoFocus}
            required={required}
            requiredMessage={validationIsRequiredMessage}
            marker={props.disableChangeMarker ? undefined : valueEdited}
            editCancelled={editCancelled}
            onlyValue={props.onlyValue}
            hideOutline={props.hideOutline}
            debounceDelayTime={props.debounceDelayTime}
            hideClearIcon={props.hideClearIcon}
            rightAlign={props.rightAlign}
            disableInputPaddingRight={props.disableInputPaddingRight}
            disableFutureDates={props.disableFutureDates}
            rightPadding={props.rightPadding}
            formControlMargin={props.formControlMargin}
            fullWidth={props.fullWidth}
            errorMessage={errorMessage}
            warningMessage={warningMessage}
            hintMessage={hintMessage}
            lineMarker={props.lineMarker}
            inputRef={props.inputRef}
            disableDisabledBackgroundColor={
              props.disableDisabledBackgroundColor
            }
            dataLoading={dataLoading}
            disableTabbing={props.disableTabbing}
            multiline={props.multiline}
            maxRows={props.maxRows}
            maxInputLength={props.maxInputLength}
            minRows={props.minRows}
            grayTextColor={props.grayTextColor}
            tooltipRensa={props.tooltipRensa}
            savedValue={savedValue}
            calcFocusedChangeMarker
            disableChangeMarker={props.disableChangeMarker}
            editComponent={
              props.editComponent as
                | React.ComponentType<
                    React.PropsWithChildren<
                      IEditComponentProps<any, undefined, undefined, undefined>
                    >
                  >
                | undefined
            }
            paddingRight={props.paddingRight}
            defaultClearValue={props.defaultClearValue}
            options={props.options}
            onBeforeClear={props.onBeforeClear}
          />
        );
      } else {
        return null;
      }
    }
    return (
      <InternalError
        id={'04a1c9a1-5d80-40ca-a11f-4a8191a016b7'}
        message={`Fel vid hämtning av attributinformation ${attributeResultError}`}
      />
    );
  }
  return (
    <InternalError
      id={'64930cc0-6553-4b29-93c9-7f249c69a916'}
      message="Attribut kan inte visas utanför BusinessDataFormContext"
    />
  );
}

const MemoBaseAttribut: React.FC<
  React.PropsWithChildren<IBaseAttributProps<any>>
> = memo(BaseAttribut);

export function Attribut<T>(
  props: IAttributProps<T>
): React.ReactElement<any> | null {
  const attributeValues = useAttributeValues(props.name as string);
  return <MemoBaseAttribut attributeValues={attributeValues} {...props} />;
}

export default Attribut;
